import React, { useState, useCallback, useMemo } from 'react';
import { useLeadTimeAlerts, useReserveTaskMaterial, useStockLevels } from '@hooks/api/warehouse';
import { LeadTimeAlert, StockLevel } from '@models/warehouse.model';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { ReceiveStockModal } from '@components/organisms/ReceiveStockModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { formatDateOnly } from '@utils/formatDate';
import { getAlertColors } from '@utils/leadTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import TimelineIcon from '@mui/icons-material/Timeline';
import FolderIcon from '@mui/icons-material/Folder';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

interface AlertCardProps {
  alert: LeadTimeAlert;
  hasEnoughStock: boolean;
  availableStock: number;
  onOrder: (catalogId: string, taskMaterialId: string) => void;
  onReserve: (alert: LeadTimeAlert) => void;
}

const AlertCard: React.FC<AlertCardProps> = ({ alert, hasEnoughStock, availableStock, onOrder, onReserve }) => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const tProjects = useTranslation('projects');
  const [expanded, setExpanded] = useState(false);
  const history = useHistory();
  const colors = getAlertColors(alert.alertLevel);

  // Sprint 30 change request (story-152 QA re-review, Bug #1). Was built as English template
  // literals with interpolation (guard blind spot #2) — each count/branch now reads its own full
  // catalog string per house style §10.5/§11.1, not a shared template with an English suffix.
  const overdueDays = Math.abs(alert.daysUntilDeadline);
  const deadlineLabel =
    alert.alertLevel === 'OVERDUE'
      ? (overdueDays === 1 ? tWarehouse.alerts.overdueByOne : tWarehouse.alerts.overdueByOther).replace(
          '{n}',
          String(overdueDays),
        )
      : alert.daysUntilDeadline === 0
        ? tWarehouse.alerts.orderToday
        : (alert.daysUntilDeadline === 1
            ? tWarehouse.alerts.daysLeftOne
            : tWarehouse.alerts.daysLeftOther
          ).replace('{n}', String(alert.daysUntilDeadline));

  // Timeline data for expanded view
  const today = new Date();
  const orderByDate = new Date(alert.orderByDate);
  const taskStartDate = new Date(alert.taskStartDate);
  const totalDays = Math.max(1, Math.ceil((taskStartDate.getTime() - orderByDate.getTime()) / (1000 * 60 * 60 * 24)));

  return (
    <div className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-200 overflow-hidden`}>
      <div
        className={`${colors.bg} border-l-4 ${colors.border} p-3 cursor-pointer`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
              <Inventory2Icon className={colors.text} sx={{ fontSize: 20 }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{alert.materialName}</h3>
              <p className="text-xs text-gray-400">
                {alert.taskName}
                {alert.projectName && ` - ${alert.projectName}`}
              </p>
            </div>
            <ExpandMoreIcon
              className="text-gray-400 transition-transform duration-200"
              sx={{ fontSize: 20, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tCommon.quantity}</p>
              <p className="text-base font-semibold text-gray-900">
                {alert.quantity} {alert.unit || ''}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.orderBy}</p>
              <p className={`text-base font-bold ${colors.text}`}>
                {formatDateOnly(orderByDate)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.taskStarts}</p>
              <p className="text-base font-semibold text-gray-900">
                {formatDateOnly(taskStartDate)}
              </p>
            </div>
            {alert.supplier && (
              <div className="text-center">
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.supplier}</p>
                <p className="text-sm font-medium text-gray-900">{alert.supplier}</p>
              </div>
            )}
            <button type="button"
              onClick={(e) => { e.stopPropagation(); history.push(`/projects?taskId=${alert.taskId}`); }}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              title={tWarehouse.alerts.viewTask}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </button>
            {alert.catalogId && hasEnoughStock && (
              <Button
                variant="primary"
                leftIcon={<BookmarkAddIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onReserve(alert); }}
                className="!bg-green-600 hover:!bg-green-700"
              >
                {tWarehouse.alerts.reserve}
              </Button>
            )}
            {alert.catalogId && !hasEnoughStock && (
              <Button
                variant="primary"
                leftIcon={<AddShoppingCartIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onOrder(alert.catalogId!, alert.taskMaterialId); }}
                className={
                  alert.alertLevel === 'OVERDUE'
                    ? '!bg-red-600 hover:!bg-red-700'
                    : alert.alertLevel === 'URGENT'
                      ? '!bg-amber-500 hover:!bg-amber-600'
                      : '!bg-blue-600 hover:!bg-blue-700'
                }
              >
                {alert.alertLevel === 'OVERDUE' ? tWarehouse.alerts.orderNow : tWarehouse.alerts.orderAction}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
            <ScheduleIcon sx={{ fontSize: 12, marginRight: '4px' }} />
            {deadlineLabel} {tWarehouse.alerts.leadTimeSuffix.replace('{n}', String(alert.leadTimeDays))}
          </span>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 space-y-4">
          {/* Timeline visualization */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TimelineIcon className="text-gray-400" sx={{ fontSize: 16 }} />
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.orderTimeline}</p>
            </div>
            <div className="relative flex items-center gap-0 h-8">
              {/* Order By marker */}
              <div className="flex flex-col items-center z-10">
                <div className={`w-3 h-3 rounded-full ${alert.alertLevel === 'OVERDUE' ? 'bg-red-500' : 'bg-blue-500'} ring-2 ring-white`} />
                <p className="text-[10px] text-gray-500 mt-1 whitespace-nowrap">{tWarehouse.alerts.orderByMarker}</p>
              </div>
              {/* Line */}
              <div className="flex-1 h-0.5 bg-gray-300 mx-1" />
              {/* Today marker (if between order by and task start) */}
              {today >= orderByDate && today <= taskStartDate && (
                <>
                  <div className="flex flex-col items-center z-10">
                    <div className="w-3 h-3 rounded-full bg-indigo-500 ring-2 ring-white" />
                    <p className="text-[10px] text-indigo-600 font-semibold mt-1 whitespace-nowrap">{tWarehouse.alerts.today}</p>
                  </div>
                  <div className="flex-1 h-0.5 bg-gray-300 mx-1" />
                </>
              )}
              {/* Task Starts marker */}
              <div className="flex flex-col items-center z-10">
                <div className="w-3 h-3 rounded-full bg-green-500 ring-2 ring-white" />
                <p className="text-[10px] text-gray-500 mt-1 whitespace-nowrap">{tWarehouse.alerts.taskStartsMarker}</p>
              </div>
            </div>
          </div>

          {/* Detail Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Stock Availability */}
            <div className="flex items-start gap-2">
              <Inventory2Icon className="text-gray-400 mt-0.5" sx={{ fontSize: 16 }} />
              <div>
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.availableStock}</p>
                <p className={`text-sm font-semibold ${availableStock >= alert.quantity ? 'text-green-600' : 'text-red-600'}`}>
                  {availableStock} {alert.unit || ''}
                </p>
                {availableStock < alert.quantity && (
                  <p className="text-[10px] text-red-500">
                    {tWarehouse.alerts.needMore.replace('{n}', String(alert.quantity - availableStock))}
                  </p>
                )}
              </div>
            </div>

            {/* Warehouse */}
            {alert.warehouseId && (
              <div className="flex items-start gap-2">
                <WarehouseIcon className="text-gray-400 mt-0.5" sx={{ fontSize: 16 }} />
                <div>
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.warehouseLabel}</p>
                  <p className="text-sm font-medium text-gray-900">{alert.warehouseId ? tWarehouse.alerts.assigned : '—'}</p>
                </div>
              </div>
            )}

            {/* Lead Time Breakdown */}
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.leadTime}</p>
              <p className="text-sm font-medium text-gray-900">{alert.leadTimeDays} {tWarehouse.alerts.workingDays}</p>
              <p className="text-[10px] text-gray-500">
                {totalDays} {tWarehouse.alerts.calendarDaysToTask}
              </p>
            </div>

            {/* Supplier */}
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.supplier}</p>
              <p className="text-sm font-medium text-gray-900">{alert.supplier || '—'}</p>
            </div>

            {/* Project Link */}
            {alert.projectName && (
              <div className="flex items-start gap-2">
                <FolderIcon className="text-gray-400 mt-0.5" sx={{ fontSize: 16 }} />
                <div>
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tProjects.project}</p>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); history.push(`/projects/${alert.projectId}`); }}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                  >
                    {alert.projectName}
                  </button>
                </div>
              </div>
            )}

            {/* Task Link */}
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{tWarehouse.alerts.taskLabel}</p>
              <button type="button"
                onClick={(e) => { e.stopPropagation(); history.push(`/projects?taskId=${alert.taskId}`); }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left"
              >
                {alert.taskName}
              </button>
            </div>

            {/* Status Summary */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${colors.bg}`}>
                <ScheduleIcon className={colors.text} sx={{ fontSize: 14 }} />
                <span className={`text-xs font-medium ${colors.text}`}>
                  {alert.alertLevel === 'OVERDUE'
                    ? (overdueDays === 1
                        ? tWarehouse.alerts.statusOverdueOne
                        : tWarehouse.alerts.statusOverdueOther
                      ).replace('{n}', String(overdueDays))
                    : alert.alertLevel === 'URGENT'
                      ? (alert.daysUntilDeadline === 1
                          ? tWarehouse.alerts.statusUrgentOne
                          : tWarehouse.alerts.statusUrgentOther
                        ).replace('{n}', String(alert.daysUntilDeadline))
                      : (alert.daysUntilDeadline === 1
                          ? tWarehouse.alerts.statusUpcomingOne
                          : tWarehouse.alerts.statusUpcomingOther
                        ).replace('{n}', String(alert.daysUntilDeadline))
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LeadTimeAlertsSection: React.FC = () => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const { data: alerts = [], isLoading, refetch } = useLeadTimeAlerts();
  const { data: stockLevels = [] } = useStockLevels();
  const reserveTaskMaterial = useReserveTaskMaterial();
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | undefined>();
  const [selectedTaskMaterialId, setSelectedTaskMaterialId] = useState<string | undefined>();
  const [confirmReserveAlert, setConfirmReserveAlert] = useState<LeadTimeAlert | null>(null);

  // Build a map of available stock per catalogId+warehouseId
  const stockMap = useMemo(() => {
    const map = new Map<string, number>();
    stockLevels.forEach((sl: StockLevel) => {
      const key = `${sl.catalogId}_${sl.warehouseId}`;
      const available = (sl.quantityOnHand || 0) - (sl.quantityReserved || 0);
      map.set(key, (map.get(key) || 0) + available);
    });
    return map;
  }, [stockLevels]);

  const getAvailableStock = useCallback((alert: LeadTimeAlert): number => {
    if (!alert.catalogId || !alert.warehouseId) return 0;
    return stockMap.get(`${alert.catalogId}_${alert.warehouseId}`) || 0;
  }, [stockMap]);

  // Order flow (no stock available)
  const handleOrder = useCallback((catalogId: string, taskMaterialId: string) => {
    setSelectedCatalogId(catalogId);
    setSelectedTaskMaterialId(taskMaterialId);
    setIsReceiveModalOpen(true);
  }, []);

  const handleCloseReceiveModal = useCallback(() => {
    setIsReceiveModalOpen(false);
    setSelectedCatalogId(undefined);
    setSelectedTaskMaterialId(undefined);
  }, []);

  const handleReceiveSuccess = useCallback(async () => {
    if (selectedTaskMaterialId) {
      try {
        await reserveTaskMaterial.mutateAsync(selectedTaskMaterialId);
      } catch {
        toast.warn(tWarehouse.alerts.receiveReserveFailWarn);
      }
    }
    refetch();
  }, [selectedTaskMaterialId, reserveTaskMaterial, refetch, tWarehouse]);

  // Reserve flow (stock already available)
  const handleReserve = useCallback((alert: LeadTimeAlert) => {
    setConfirmReserveAlert(alert);
  }, []);

  const handleConfirmReserve = useCallback(async () => {
    if (!confirmReserveAlert) return;
    try {
      await reserveTaskMaterial.mutateAsync(confirmReserveAlert.taskMaterialId);
    } catch {
      toast.warn(tWarehouse.alerts.reserveFailWarn);
    }
    setConfirmReserveAlert(null);
    refetch();
  }, [confirmReserveAlert, reserveTaskMaterial, refetch, tWarehouse]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loading size="lg" center />
      </div>
    );
  }

  if (alerts.length === 0) return null;

  const overdueAlerts = alerts.filter((a) => a.alertLevel === 'OVERDUE');
  const urgentAlerts = alerts.filter((a) => a.alertLevel === 'URGENT');
  const upcomingAlerts = alerts.filter((a) => a.alertLevel === 'UPCOMING');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ScheduleIcon className="text-indigo-500" sx={{ fontSize: 22 }} />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {tWarehouse.alerts.materialOrderDeadlines}
          </h2>
          <p className="text-xs text-gray-400">
            {tWarehouse.alerts.materialOrderDeadlinesSubtitle}
          </p>
        </div>
      </div>

      {/* Overdue */}
      {overdueAlerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wider flex items-center gap-2">
            {tWarehouse.alerts.overdue} ({overdueAlerts.length})
          </h3>
          {overdueAlerts.map((alert) => (
            <AlertCard key={alert.taskMaterialId} alert={alert} hasEnoughStock={getAvailableStock(alert) >= alert.quantity} availableStock={getAvailableStock(alert)} onOrder={handleOrder} onReserve={handleReserve} />
          ))}
        </div>
      )}

      {/* Urgent */}
      {urgentAlerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-2">
            {tWarehouse.alerts.urgentOrderSoon} ({urgentAlerts.length})
          </h3>
          {urgentAlerts.map((alert) => (
            <AlertCard key={alert.taskMaterialId} alert={alert} hasEnoughStock={getAvailableStock(alert) >= alert.quantity} availableStock={getAvailableStock(alert)} onOrder={handleOrder} onReserve={handleReserve} />
          ))}
        </div>
      )}

      {/* Upcoming */}
      {upcomingAlerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-2">
            {tWarehouse.alerts.upcoming} ({upcomingAlerts.length})
          </h3>
          {upcomingAlerts.map((alert) => (
            <AlertCard key={alert.taskMaterialId} alert={alert} hasEnoughStock={getAvailableStock(alert) >= alert.quantity} availableStock={getAvailableStock(alert)} onOrder={handleOrder} onReserve={handleReserve} />
          ))}
        </div>
      )}

      {/* Receive Stock Modal */}
      <ReceiveStockModal
        isOpen={isReceiveModalOpen}
        onClose={handleCloseReceiveModal}
        initialCatalogId={selectedCatalogId}
        onSuccess={handleReceiveSuccess}
      />

      {/* Reserve Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmReserveAlert}
        onClose={() => setConfirmReserveAlert(null)}
        onConfirm={handleConfirmReserve}
        variant="info"
        title={tWarehouse.alerts.reserveMaterialTitle}
        description={
          confirmReserveAlert
            ? `Reserve ${confirmReserveAlert.quantity} ${confirmReserveAlert.unit || ''} of ${confirmReserveAlert.materialName} for task "${confirmReserveAlert.taskName}"?`
            : ''
        }
        confirmText={tWarehouse.alerts.reserve}
        cancelText={tCommon.cancel}
      />
    </div>
  );
};

export default LeadTimeAlertsSection;
